// @flow

import {
  SURVEY_LAYER_CATEGORIES,
  SURVEY_LAYER_VALUES,
  SURVEY_LAYERS,
  SURVEY_SOIL_CLASS_LAYERS,
} from '../../constants/SurveyLayers';
import {
  calculateIntervalAreas,
  calculateIntervals,
  calculateOverridesArea,
  initializeOverrides,
} from 'js/components/Prescription/PrescriptionUtils';
import { PRESCRIPTION_METERING } from 'js/constants/PrescriptionConstants';
import UUID from 'js/algorithms/UUID';
import NumberUtils from 'js/helpers/NumberUtils';
import { generateLUTArrayForLayer } from 'js/constants/ColorMaps';
import vitalityIcon from 'style/images/layers/vitality-icon.png';
import variationsIcon from 'style/images/layers/variance-icon.png';
import visibilityIcon from 'style/images/layers/visible-icon.png';
import { PRESCRIPTION_UNIT } from '../../constants/PrescriptionConstants';
import type { LAYER_CONFIG } from '../../model/surveys/SurveyLayerConfig';
import type { LanguageFile } from '../../helpers/LanguageUtils';
import { getSourceCategory } from '../../helpers/SurveyUtils';
import { SATELLITE_LAYERS } from '../../constants/SatelliteLayers';
import { YIELD_LAYER, YIELD_LAYERS } from '../../constants/YieldLayer';
import { isDemandLayerType } from '../../model/surveys/country/SurveyCountryStrategy';
import { mapSeedingCalculatorInputsFromApi } from '../../reducers/MapSeedingCalcInput';
import PrescriptionModelReference from '../../model/prescription/PrescriptionModelReference';
import { fillNullValuesInsideField, isNullInterval } from './nullValueRegions';
import { SeedingCalculator } from './SeedingCalculator';
// export const SATELLITE_LAYERS = {
//   vitality: "vitality",
//   variations: "variations",
//   visible: "visible",
// };

// export const SATELLITE_LAYER_LUT = {
//   [SATELLITE_LAYERS.VITALITY_NDVI]: vitalityNDVILUT,
//   [SATELLITE_LAYERS.VARIATIONS_NDVI]: variationsNDVILUT,
// };

export const SATELLITE_LAYER_ICON = {
  [SATELLITE_LAYERS.VITALITY_NDVI]: vitalityIcon,
  [SATELLITE_LAYERS.VITALITY_NDRE]: vitalityIcon,
  [SATELLITE_LAYERS.VARIATIONS_NDVI]: variationsIcon,
  [SATELLITE_LAYERS.VARIATIONS_NDRE]: variationsIcon,
  [SATELLITE_LAYERS.VISIBLE]: visibilityIcon,
};

export const MetaTypes = {
  LEGACY: 'LEGACY',
  FERTILIZING: 'FERTILIZING',
  SPRAYING: 'SPRAYING',
  SPOT_SPRAYING: 'SPOT_SPRAYING',
  SEEDING: 'SEEDING',
  LIME: 'LIME',
};

export const metaTypeToInitialLayerSelection = {
  [MetaTypes.FERTILIZING]: [],
  [MetaTypes.SEEDING]: [],
  [MetaTypes.LIME]: [SURVEY_LAYERS.HUMUS, SURVEY_LAYERS.CLAY],
  [MetaTypes.SPRAYING]: [],
  [MetaTypes.SPOT_SPRAYING]: [],
  [MetaTypes.LEGACY]: [],
};

export type MetaType = $Keys<typeof MetaTypes>;
export type SATELLITE_LAYER_TYPE = $Keys<typeof SATELLITE_LAYERS>;
export type Layer = $Keys<typeof SATELLITE_LAYERS> | $Keys<typeof SURVEY_LAYERS>;

export const SOURCE_CATEGORY = {
  ['VITALITY']: SATELLITE_LAYERS.VITALITY_NDVI,
  ['NUTRIENT']: SURVEY_LAYER_CATEGORIES.NUTRIENT,
  ['TEXTURE']: SURVEY_LAYER_CATEGORIES.TEXTURE,
  ['TOPOGRAPHY']: SURVEY_LAYER_CATEGORIES.TOPOGRAPHY,
  ['YIELD']: YIELD_LAYER.YIELD,
};
export type SourceCategory = $Keys<typeof SOURCE_CATEGORY>;

export const LAYER_CATEGORY = Object.freeze({
  ['SATELLITE']: 'SATELLITE',
  ['SURVEY']: 'SURVEY',
  ['YIELD']: YIELD_LAYER.YIELD,
});
export type LayerCategory = $Keys<typeof LAYER_CATEGORY>;

export class Interval {
  key: string;

  constructor(min: number, max: number, prescription: number) {
    this.min = min;
    this.max = max;
    this.prescription = prescription;
    this.key = UUID.uuidv4();
  }
}

export class ClassificationInterval extends Interval {
  key: string;

  constructor(min: number, max: number, prescription: number, classification: String) {
    super(min, max, prescription);
    this.classification = classification;
    this.key = UUID.uuidv4();
  }
}

export const LOWEST_INTERVAL_HIGH = 1000;

export class PrescriptionJob {
  jobId: number;
  jobName: string;
  fieldId: number;
  seasonId: number;
  fieldSize: number;
  bounds: {
    north: number;
    south: number;
    east: number;
    west: number;
  };
  date: Date;
  values: Array<Array<number | null>>;
  layer: Layer;
  category: SourceCategory;
  metaType: MetaType;
  maxPrescription: number;
  totalPrescription: number;
  intervals: Array<Interval>;
  overrides: Array<Array<number>>;
  overrideAreas: Object;
  metering: string;
  unit: string;
  createdDate: Date;
  lastModifiedDate: Date;
  saved: boolean;
  legacy: boolean;
  images: Object;
  classifications: Object;
  layerType: string;
  limeInfo: {
    tolerance: number;
    efficiency: number;
  } | null;
  vrmSet: {
    sourceVrmId: string;
    jobIndex: number;
  } | null;
  polygon: string;
  usingJobPolygon: boolean;
  seedingCalculatorParams: SeedingCalculator | null;
  layerValues: Object | null;
  surveyImageId: number | string | null;
  forceRecalculated: boolean | undefined;
  prescriptionModel: PrescriptionModelReference | null;

  static fromNdviData(data, fields): PrescriptionJob {
    const {
      vrmName,
      date,
      ndviIntervals,
      overrides,
      id,
      allocationUnit,
      seasonId,
      values,
      polygon,
    } = data;
    const { fieldId, metaType, maxAllocation, metering, metaTypeInfo } = data.metaData;
    const field = fields.get(fieldId);

    const intervals = ndviIntervals.map((i) => new Interval(i.min, i.max, i.allocation));
    const tiffs = data.sourceTiffs;
    const savedDate = Array.isArray(tiffs) && tiffs.length > 0 ? tiffs[0].date : null;
    let assets;
    if (data.assets) {
      assets = {
        [SATELLITE_LAYERS.VITALITY_NDVI]: data.assets['vitality'],
        [SATELLITE_LAYERS.VARIATIONS_NDVI]: data.assets['variations'],
        [SATELLITE_LAYERS.VISIBLE]: data.assets[SATELLITE_LAYERS.VISIBLE.toLowerCase()],
      };
    }

    let newJob = new PrescriptionJob();

    newJob.fieldId = fieldId;
    newJob.fieldSize = field?.size;
    newJob.bounds = field?.bounds;
    newJob.jobId = id;
    newJob.jobName = vrmName;
    newJob.seasonId = seasonId;
    newJob.layer = SATELLITE_LAYERS.VITALITY_NDVI;
    newJob.category = getSourceCategory(SATELLITE_LAYERS.VITALITY_NDVI);
    newJob.date = savedDate ? savedDate : new Date(date);
    newJob.metaType = metaType;
    newJob.metering = metering || PRESCRIPTION_METERING.TOTAL;
    newJob.intervals = intervals;
    newJob.values = values;
    if (intervals.some(isNullInterval) && field?.polygon?.coordinates) {
      newJob.values = fillNullValuesInsideField(values, newJob.bounds, field.polygon.coordinates);
    }
    newJob.overrides = overrides;
    newJob.unit = allocationUnit || PRESCRIPTION_UNIT.KILOGRAM;
    newJob.overrides = initializeOverrides(newJob); // make sure it is properly formed
    newJob.maxPrescription = maxAllocation;
    newJob.overrideAreas = calculateOverridesArea(newJob);
    newJob = calculateIntervalAreas(newJob);
    newJob.assets = assets;
    newJob.polygon = polygon;
    if (!newJob.intervals || (Array.isArray(newJob.intervals) && newJob.intervals.length === 0)) {
      newJob.intervals = calculateIntervals(newJob);
    }
    newJob.saved = false;
    newJob.seedingCalculatorParams = null;

    if (metaTypeInfo) {
      newJob.seedingCalculatorParams = metaTypeInfo.seedingCalculatorInfo
        ? mapSeedingCalculatorInputsFromApi(metaTypeInfo.seedingCalculatorInfo)
        : null;
    }

    return newJob;
  }

  static fromSurveyData(data, fields): PrescriptionJob {
    const field = fields.get(data.fieldId);
    let newJob = new PrescriptionJob();

    newJob.fieldId = data.fieldId;
    newJob.fieldSize = field?.size;
    newJob.bounds = field?.bounds;
    newJob.jobId = data.id;
    newJob.seasonId = data.seasonId;
    newJob.jobName = data.name;
    newJob.category = getSourceCategory(data.layer);
    newJob.createdDate = new Date(data.created);
    newJob.lastModifiedDate = data.lastModified ? new Date(data.lastModified) : null;
    newJob.layer = data.layer;
    newJob.unit = data.unit || PRESCRIPTION_UNIT.KILOGRAM;
    newJob.values = data.layerValues;
    newJob.layerType = data.layerType;
    newJob.metaType = data.metaType;
    newJob.intervals = data.intervals.map((i) => {
      if (i.classification != null) {
        return new ClassificationInterval(i.min, i.max, i.allocation, i.classification);
      }

      // Persisted demand jobs don't carry .classification on each interval
      // (the backend doesn't echo it back). Reconstruct it from the fine
      // classification mapping using the interval's bucket index, since
      // demand intervals are persisted as index ± 0.5 (see
      // calculateClassificationIntervals in PrescriptionUtils.js).
      const fineMappings = data.layerMappings?.FINE;
      if (fineMappings && isDemandLayerType(data.layerType)) {
        const bucketIndex = Math.round((i.min + i.max) / 2);
        const classification = Object.keys(fineMappings).find((key) =>
          fineMappings[key].includes(bucketIndex)
        );
        if (classification) {
          return new ClassificationInterval(i.min, i.max, i.allocation, classification);
        }
      }

      return new Interval(i.min, i.max, i.allocation);
    });
    if (newJob.intervals.some(isNullInterval) && field?.polygon?.coordinates) {
      newJob.values = fillNullValuesInsideField(
        newJob.values,
        newJob.bounds,
        field.polygon.coordinates
      );
    }
    newJob.overrides = data.overrides;
    newJob.overrides = initializeOverrides(newJob); // make sure it is properly formed
    newJob.totalPrescription = data.totalPrescription;
    newJob.maxPrescription = data.maxPrescription;
    newJob.overrideAreas = calculateOverridesArea(newJob);
    newJob.polygon = data.polygon;
    if (data.metaType !== MetaTypes.LIME) {
      newJob = calculateIntervalAreas(newJob);
    }
    newJob.metering = data.metering || PRESCRIPTION_METERING.TOTAL;
    newJob.assets = data.images;
    newJob.saved = false;
    newJob.limeInfo = data.limeInfo ? data.limeInfo : null;
    newJob.vrmSet = data.vrmSet != null ? data.vrmSet : null;
    newJob.surveyImageId = data.surveyImageId;
    // legacy survey jobs
    newJob.surveyId = data.surveyId;
    if (isDemandLayerType(newJob.layerType)) {
      newJob.classifications = {
        values: data.layerValues,
        mappings: data.layerMappings,
      };
    }
    newJob.seedingCalculatorParams = data.seedingCalculatorInfo
      ? mapSeedingCalculatorInputsFromApi(data.seedingCalculatorInfo)
      : null;
    newJob.prescriptionModel = data.prescriptionModel
      ? PrescriptionModelReference.fromApi(data.prescriptionModel)
      : null;
    return newJob;
  }

  static fromArchive(job, fields, legacy): PrescriptionJob {
    const {
      id,
      fieldId,
      vrmName,
      metaType,
      createdOn,
      seasonId,
      vrmSet,
      totalAllocation,
      allocationUnit,
      seedingCalculatorInfo,
      prescriptionModel,
    } = job;

    const field = fields.get(fieldId);

    if (!field && !fieldId) {
      return null;
    }

    const newJob = new PrescriptionJob();

    newJob.fieldId = field ? field.fieldId : fieldId;
    newJob.bounds = field ? field.bounds : null;
    newJob.fieldSize = field ? field.size : 0;
    newJob.jobId = id;
    newJob.metaType = metaType;
    newJob.legacy = legacy;
    newJob.jobName = vrmName;
    newJob.createdDate = new Date(createdOn);
    newJob.layer = SATELLITE_LAYERS.VITALITY_NDVI;
    newJob.seasonId = seasonId;
    newJob.vrmSet = vrmSet;
    newJob.totalPrescription = totalAllocation;
    newJob.allocationUnit = allocationUnit;
    newJob.seedingCalculatorParams = seedingCalculatorInfo
      ? mapSeedingCalculatorInputsFromApi(seedingCalculatorInfo)
      : null;
    newJob.prescriptionModel = prescriptionModel
      ? PrescriptionModelReference.fromApi(prescriptionModel)
      : null;

    return newJob;
  }

  static fromSurveyArchive(job, fields): PrescriptionJob {
    const {
      id,
      fieldId,
      layer,
      name,
      metaType,
      created,
      seasonId,
      type,
      vrmSet,
      totalPrescription,
      unit,
      prescriptionModel,
      seedingCalculatorInfo,
    } = job;
    const field = fields.get(fieldId);

    if (!field && !fieldId) {
      return null;
    }

    const newJob = new PrescriptionJob();

    newJob.fieldId = field ? field.fieldId : fieldId;
    newJob.bounds = field ? field.bounds : null;
    newJob.fieldSize = field ? field.size : 0;
    newJob.jobId = id;
    newJob.metaType = metaType;
    newJob.jobName = name;
    newJob.createdDate = new Date(created);
    newJob.layer = layer;
    newJob.seasonId = seasonId;
    newJob.vrmSet = vrmSet;
    newJob.totalPrescription = totalPrescription;
    newJob.unit = unit;
    if (isDemandLayerType(type)) {
      newJob.layerType = type;
    }
    newJob.seedingCalculatorParams = seedingCalculatorInfo
      ? mapSeedingCalculatorInputsFromApi(seedingCalculatorInfo)
      : null;
    newJob.prescriptionModel = prescriptionModel
      ? PrescriptionModelReference.fromApi(prescriptionModel)
      : null;

    return newJob;
  }

  static cloneJob(job: PrescriptionJob) {
    return Object.assign(Object.create(Object.getPrototypeOf(job)), job);
  }
}

export class Survey {
  surveyImageId: number;
  farmId: number;
  fieldId: number;
  images: { key: string };
  publishDate: Date;
}

export class PrescriptionJobPostBody {
  name: string;
  surveyImageId: number | null;
  fieldId: number;
  layer: Layer;
  metaType: MetaType;
  maxPrescription: number;
  totalPrescription: number;
  unit: string;
  intervals: Array<Interval>;
  overrides: Array<Array<number>>;
  layerType: string;
  limeInfo: object | null;
  // legacy
  surveyId: string | null;
}

export class PrescriptionJobPutBody {
  name: string;
  layer: Layer;
  metaType: MetaType;
  maxPrescription: number;
  totalPrescription: number;
  seasonId: number;
  unit: string;
  intervals: Array<Interval>;
  overrides: Array<Array<number>>;
  limeInfo: object | null;
  surveyImageId: number | null;
  surveyId: string | null;
}

export const getSatelliteConfig = (layer: SATELLITE_LAYER_TYPE): LAYER_CONFIG => {
  const minValue = 0;
  const maxValue = 1;

  const lut = generateLUTArrayForLayer(layer);
  const icon = SATELLITE_LAYER_ICON[layer];

  const mapValue = (val, outMin, outMax) => {
    return NumberUtils.limitMap(val, minValue, maxValue, outMin, outMax);
  };

  const reverseMapValue = (val, inMin, inMax) => {
    return NumberUtils.limitMap(val, inMin, inMax, minValue, maxValue);
  };

  return {
    minValue: minValue,
    maxValue: maxValue,
    getName: (LangFile: LanguageFile) =>
      LangFile.PrescriptionJob.satelliteLayers[getSatelliteNameKey(layer)],
    getShorthand: (LangFile: LanguageFile) =>
      LangFile.PrescriptionJob.satelliteLayers[getSatelliteShorthandKey(layer)],
    mapValue: mapValue,
    reverseMapValue: reverseMapValue,
    icon: icon,
    getColor: (val) => {
      return lut[Math.floor(val * 255)];
    },
  };
};

const getSatelliteNameKey = (layer) => {
  switch (layer) {
    case SATELLITE_LAYERS.VITALITY_NDVI:
    case SATELLITE_LAYERS.VITALITY_NDRE:
      return 'vitality';
    case SATELLITE_LAYERS.VARIATIONS_NDVI:
    case SATELLITE_LAYERS.VARIATIONS_NDRE:
      return 'variations';
    default:
      return 'visible';
  }
};

export const getSatelliteShorthandKey = (layer) => {
  switch (layer) {
    case SATELLITE_LAYERS.VITALITY_NDVI:
    case SATELLITE_LAYERS.VARIATIONS_NDVI:
      return 'ndvi';
    case SATELLITE_LAYERS.VITALITY_NDRE:
    case SATELLITE_LAYERS.VARIATIONS_NDRE:
      return 'ndre';
    default:
      return;
  }
};

export const isSurveyLayer = (layer) => {
  return SURVEY_LAYER_VALUES.includes(layer.toUpperCase());
};

export const isSatelliteLayer = (layer) => {
  return Object.keys(SATELLITE_LAYERS).includes(layer);
};

export const isYieldLayer = (layer) => {
  return Boolean(layer) && Object.keys(YIELD_LAYERS).includes(layer.toUpperCase());
};

export const isSoilLayer = (layer) => {
  return SURVEY_SOIL_CLASS_LAYERS.includes(layer.toUpperCase());
};

export const parseMetaType = (metaType, LangFile) => {
  switch (metaType) {
    case MetaTypes.FERTILIZING: {
      return LangFile.PrescriptionJob.metaTypes.fertilizing;
    }
    case MetaTypes.SPRAYING: {
      return LangFile.PrescriptionJob.metaTypes.spraying;
    }
    case MetaTypes.SPOT_SPRAYING: {
      return LangFile.PrescriptionJob.metaTypes.spotSpraying;
    }
    case MetaTypes.SEEDING: {
      return LangFile.PrescriptionJob.metaTypes.seeding;
    }
    case MetaTypes.LIME: {
      return LangFile.PrescriptionJob.metaTypes.lime;
    }
    default: {
      return LangFile.PrescriptionJob.metaTypes.unknown;
    }
  }
};

export const parseLayerName = (layer, LangFile) => {
  if (isSurveyLayer(layer)) {
    const strings = LangFile.SurveyLayers[layer];
    return strings && strings.name;
  } else {
    let title = LangFile.PrescriptionJob.satelliteLayers[getSatelliteNameKey(layer)];
    const shorthand = LangFile.PrescriptionJob.satelliteLayers[getSatelliteShorthandKey(layer)];
    if (shorthand) {
      title = title + ` (${shorthand})`;
    }
    return title;
  }
};
